import React, {forwardRef, useEffect, useMemo, useState} from "react";
import {Box, CircularProgress, Divider, Stack, Typography} from "@mui/material";
import {endOfMonth, format} from "date-fns";
import {Bar, BarChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,} from "recharts";
import {getMetricData, getMetricGraphData} from "../../api/metric";
import {getAdvancedMetricSummary,} from "../../api/expenseBreakdown";
import {fetchJournalEntries} from "../../api/transactions";
import {getRangeAdvancedMetricSummary, getRangeMetricData, getRangeMetricGraphData,} from "../../api/reportRange";

/* ─── helpers ─── */
const currency = (v) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);

const currencyShort = (v) => {
  const n = Math.abs(v || 0);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return currency(v);
};

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const normaliseAbs = (v) => Math.abs(toNumber(v));

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return format(d, "MMM yyyy");
  } catch {
    return dateStr;
  }
};

const FONT = "'Rethink Sans', Helvetica, sans-serif";
const NAVY = "#0B1F3A";
const PRIMARY = "#536DEC";

const DRIVERS = [
  { type: "team", label: "Team Expenses" },
  { type: "marketing", label: "Marketing Expenses" },
  { type: "overhead", label: "Operations Expenses" },
  { type: "cogs", label: "Product Expenses" },
];

/* ─── sub-components ─── */

const KpiCard = ({ label, value, change, icon, periodLabel = "last month" }) => {
  const isPositive = change >= 0;
  const arrow = isPositive ? "↑" : "↓";
  const color = isPositive ? "#2E7D32" : "#C62828";
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        p: "16px 20px",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "rgba(0,0,0,0.55)" }}>
          {label}
        </Typography>
        {icon && (
          <Box
            component="img"
            src={icon}
            alt=""
            sx={{ width: 28, height: 28, objectFit: "contain", opacity: 0.85 }}
          />
        )}
      </Stack>
      <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
        {currency(value)}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color, fontWeight: 500 }}>
        {arrow} {Math.abs(change).toFixed(0)}% vs {periodLabel}
      </Typography>
    </Box>
  );
};

const DriverCard = ({ label, value, change, periodLabel = "last month" }) => {
  const isPositive = change >= 0;
  const arrow = isPositive ? "↑" : "↓";
  const color = isPositive ? "#C62828" : "#2E7D32"; // expenses: up=bad, down=good
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "rgba(0,0,0,0.55)", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
        {currency(value)}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color, fontWeight: 500, mt: 0.25 }}>
        {arrow} {Math.abs(change).toFixed(0)}% vs {periodLabel}
      </Typography>
    </Box>
  );
};

const SectionTitle = ({ children }) => (
  <Box sx={{ position: "relative", pt: 2, pb: 1.5 }}>
    <Typography
      component="span"
      sx={{
        position: "absolute",
        left: 0,
        top: -20,
        transform: "translateY(-50%)",
        display: "inline-flex",
        alignItems: "center",
        bgcolor: NAVY,
        color: "#fff",
        borderRadius: "6px",
        px: 1.5,
        py: 0.5,
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <Box sx={{ bgcolor: "#fff", px: 1.5, py: 1, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", fontSize: 12, fontFamily: FONT }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 12, mb: 0.5 }}>{d.label}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: PRIMARY }}>Income: {currency(d.income)}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#E53935" }}>Expenses: {currency(d.expenses)}</Typography>
      <Divider sx={{ my: 0.5 }} />
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#00B050" }}>Profit: {currency(d.profit)}</Typography>
    </Box>
  );
};

/* ─── main report component ─── */

const PerformanceOverviewReport = forwardRef(function PerformanceOverviewReport(
  { customerId, startDate, endDate, reportType = "performance" },
  ref
) {
  const endDateStr = useMemo(() => {
    if (!endDate) return format(endOfMonth(new Date()), "yyyy-MM-dd");
    return endDate;
  }, [endDate]);

  // startDateStr is only set when a custom range is explicitly provided.
  const startDateStr = useMemo(() => {
    if (!startDate) return null;
    try { return format(new Date(startDate + "T00:00:00"), "yyyy-MM-dd"); }
    catch { return null; }
  }, [startDate]);

  // When startDate is provided, use the range API endpoints so the report
  // reflects the full selected period rather than a single calendar month.
  const useRangeApi = !!startDateStr;

  const isMultiMonth = useRangeApi && startDateStr.substring(0, 7) !== endDateStr.substring(0, 7);
  const periodLabel = isMultiMonth ? "previous period" : "last month";

  const reportLabel = useMemo(() => {
    if (isMultiMonth) {
      return `${formatDisplayDate(startDateStr)} – ${formatDisplayDate(endDateStr)}`;
    }
    return endDate ? formatDisplayDate(endDate) : format(new Date(), "MMM yyyy");
  }, [startDateStr, endDateStr, endDate, isMultiMonth]);

  // The financial trend chart always shows the 12 months ending at endDateStr,
  // regardless of the selected report period.
  const trendStartDateStr = useMemo(() => {
    const base = endDateStr || format(new Date(), "yyyy-MM-dd");
    const end = new Date(base);
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    return format(start, "yyyy-MM-dd");
  }, [endDateStr]);

  const [loading, setLoading] = useState(false);
  const [metricData, setMetricData] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [driverData, setDriverData] = useState({});
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const sig = controller.signal;

    const load = async () => {
      setLoading(true);
      try {
        let metricRes, graphRes, growthRes, txRes, driverResults;

        if (useRangeApi) {
          // Use date-range endpoints for custom period selection.
          // Graph always spans the 12 months ending at endDateStr (trendStartDateStr),
          // regardless of the selected report window.
          [metricRes, graphRes, growthRes, txRes] = await Promise.allSettled([
            getRangeMetricData(customerId, startDateStr, endDateStr, sig),
            getRangeMetricGraphData(customerId, trendStartDateStr, endDateStr, sig),
            // getRangeGrowthPlanData(customerId, startDateStr, endDateStr, sig),
            fetchJournalEntries(customerId, { page_size: 8, ordering: "-amount", start_date: startDateStr, end_date: endDateStr }, sig),
          ]);
          driverResults = await Promise.allSettled(
            DRIVERS.map((d) => getRangeAdvancedMetricSummary(customerId, d.type, startDateStr, endDateStr, sig))
          );
        } else {
          // Use existing single-date endpoints (legacy / current-month / YTD)
          [metricRes, graphRes, growthRes, txRes] = await Promise.allSettled([
            getMetricData(customerId, endDateStr, sig, { adjusted: false, ytd: false }),
            getMetricGraphData(customerId, endDateStr, sig, { adjusted: false, ytd: false }),
            // getGrowthPlanData(customerId, endDateStr, sig, { adjusted: false, ytd: false }),
            fetchJournalEntries(customerId, { page_size: 8, ordering: "-amount" }, sig),
          ]);
          driverResults = await Promise.allSettled(
            DRIVERS.map((d) =>
              getAdvancedMetricSummary(customerId, endDateStr, d.type, sig, { adjusted: false, ytd: false })
            )
          );
        }

        if (metricRes.status === "fulfilled") setMetricData(metricRes.value?.data || null);
        if (graphRes.status === "fulfilled") setGraphData(graphRes.value?.data || null);
        if (txRes.status === "fulfilled") {
          const raw = txRes.value?.data;
          const items = Array.isArray(raw) ? raw : (raw?.results || []);
          setTransactions(items.slice(0, 8));
        }

        const nextDrivers = {};
        driverResults.forEach((r, i) => {
          if (r.status === "fulfilled") {
            nextDrivers[DRIVERS[i].type] = r.value?.data || null;
          }
        });
        setDriverData(nextDrivers);
      } catch (err) {
        if (err?.name !== "AbortError" && err?.name !== "CanceledError") {
          console.warn("Report data load error", err);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [customerId, endDateStr, startDateStr, trendStartDateStr, useRangeApi]);

  /* ── derived values ── */
  const income = normaliseAbs(metricData?.income);
  const expenses = normaliseAbs(metricData?.expense);
  const profit = income - expenses;
  const incomeChange = toNumber(metricData?.income_change_pct ?? metricData?.income_percentage ?? 0);
  const expenseChange = toNumber(metricData?.expense_change_pct ?? metricData?.expense_percentage ?? 0);
  const profitChange = toNumber(metricData?.profit_percentage ?? 0);

  const chartData = useMemo(() => {
    if (!graphData?.months?.length) return [];
    return graphData.months.map((label, i) => {
      const inc = normaliseAbs(graphData.income?.[i] ?? 0);
      const exp = normaliseAbs(graphData.expense?.[i] ?? 0);
      return { label, income: inc, expenses: exp, profit: inc - exp };
    });
  }, [graphData]);

  const today = format(new Date(), "MMMM d, yyyy");

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 400 }}>
        <CircularProgress size={32} sx={{ color: PRIMARY }} />
        <Typography sx={{ mt: 2, fontFamily: FONT, color: "text.secondary", fontSize: 14 }}>
          Loading report data…
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        bgcolor: "#F7F8FC",
        fontFamily: FONT,
        "& *": { fontFamily: `${FONT} !important`, boxSizing: "border-box" },
        "@media print": {
          bgcolor: "#F7F8FC",
          "& .no-print": { display: "none !important" },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          backgroundImage: "url('/Report_header.svg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: 140,
          aspectRatio: "595/140",
          p: { xs: "24px 28px", md: "32px 40px" },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ maxWidth: "50%", marginTop: "0.5em" }}>
          <Typography
            sx={{
              color: "#FFF",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "var(--fontFamily, \"Rethink Sans\")",
              fontSize: "31px",
              fontStyle: "italic",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            Performance{"\n"}Overview Report
          </Typography>
        </Box>
        <Stack alignItems="flex-start" spacing={1.5} style={{ marginTop: "2em" }}>
          <Box
            component="img"
            src="/lucro-logo-white.png"
            alt="Lucro"
            sx={{ height: 28, width: "auto", objectFit: "contain" }}
          />
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: "#fff" }}>
            {reportLabel}
          </Typography>
        </Stack>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: { xs: "20px 16px", md: "28px 32px" }, display: "flex", flexDirection: "column", gap: 3 }}>

        {/* ── KPI Row ── */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <KpiCard label="Total Profit" value={profit} change={profitChange} icon="/total_profit.svg" periodLabel={periodLabel} />
          <KpiCard label="Total Income" value={income} change={incomeChange} icon="/total_income.svg" periodLabel={periodLabel} />
          <KpiCard label="Total Expenses" value={expenses} change={expenseChange} icon="/total_expenses.svg" periodLabel={periodLabel} />
        </Stack>

        {/* ── Performance Drivers ── */}
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            p: "20px 24px",
            bgcolor: "#fff",
          }}
        >
          <SectionTitle>Performance Drivers</SectionTitle>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} divider={<Divider orientation="vertical" flexItem />}>
            {DRIVERS.map(({ type, label }) => {
              const summary = driverData[type];
              const val = normaliseAbs(
                summary?.current_month_total ?? summary?.actual ?? summary?.pnl_value ?? 0
              );
              const chg = toNumber(
                summary?.change_from_last_month_pct ?? summary?.change_from_last_month ?? 0
              );
              return <DriverCard key={type} label={label} value={val} change={chg} periodLabel={periodLabel} />;
            })}
          </Stack>
        </Box>

        {/* ── Chart + Transactions ── */}
        <Stack direction="column" spacing={2}>
          {/* Chart — always full width; Transactions stacks below */}
          <Box
            sx={{
              width: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              p: "20px 24px",
              bgcolor: "#fff",
              boxSizing: "border-box",
            }}
          >
            <SectionTitle>12-Month Financial Trend</SectionTitle>
            <Box sx={{ width: "100%", height: 240 }}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="99%" height="100%">
                  <BarChart
                    data={chartData}
                    barCategoryGap="18%"
                    barGap={3}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.1)" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fontFamily: FONT, fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                      tick={{ fontSize: 11, fontFamily: FONT, fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                      width={48}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(83,109,236,0.06)" }} />
                    <Bar dataKey="income" fill={PRIMARY} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expenses" fill="#E53935" radius={[3, 3, 0, 0]} />
                    <Line
                      type="linear"
                      dataKey="profit"
                      stroke="#00B050"
                      strokeWidth={2.5}
                      dot={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                  <Typography sx={{ fontFamily: FONT, color: "text.secondary", fontSize: 13 }}>
                    No chart data available
                  </Typography>
                </Stack>
              )}
            </Box>
            {/* Legend */}
            <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 1.5 }}>
              {[
                { color: PRIMARY, label: "Income" },
                { color: "#E53935", label: "Expenses" },
                { color: "#00B050", label: "Profit", isLine: true },
              ].map(({ color, label, isLine }) => (
                <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
                  {isLine ? (
                    <Box sx={{ width: 16, height: 3, bgcolor: color, borderRadius: 2 }} />
                  ) : (
                    <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: 1 }} />
                  )}
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "#6B7280" }}>
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* Significant Transactions */}
          <Box
            sx={{
              width: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              p: "20px 24px",
              bgcolor: "#fff",
              boxSizing: "border-box",
            }}
          >
            <SectionTitle>Significant Transactions</SectionTitle>
            <Stack spacing={0} divider={<Divider sx={{ borderColor: "#F3F4F6" }} />}>
              {transactions.length === 0 ? (
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "text.secondary", py: 2 }}>
                  No transactions available.
                </Typography>
              ) : (
                transactions.map((tx, i) => {
                  const amt = toNumber(tx.amount);
                  const isNeg = amt < 0;
                  return (
                    <Box key={tx.id ?? i} sx={{ py: 1.25 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "rgba(0,0,0,0.45)", mb: 0.25 }}>
                            {tx.date} · <Box component="span" sx={{ color: isNeg ? "#E53935" : PRIMARY }}>{currency(Math.abs(amt))}</Box>
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 12,
                              fontWeight: 600,
                              color: NAVY,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tx.description || tx.name || tx.vendor_name || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Box>
        </Stack>



        {/* ── Footer ── */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ pt: 1, borderTop: "1px solid #E5E7EB", mt: 1 }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "rgba(0,0,0,0.4)" }}>
            Generated on {today}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "rgba(0,0,0,0.4)" }}>
            Page 1 of 1
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
});

export default PerformanceOverviewReport;
